import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import { PersonAdd } from "@mui/icons-material";
import { Dialog } from "../../../components/Dialogs/Dialog";
import { UsersAutoSearchByRole } from "../../../components/AutoCompleteSearches/UsersAutoSearchByRole";
import { ISelectOption } from "../../../constants/types";
import { api } from "../../../api/api";
import { useDispatchWrapper } from "../../../hooks";
import { showMessage } from "../../../redux/messages/messagesActions";
import { ILead } from "../../../redux/leads/leads.types";

interface IAssignUserDialogProps {
  open: boolean;
  onClose: () => void;
  selectedLeads: ILead[];
  onComplete?: () => void;
}

export const AssignUserDialog: React.FC<IAssignUserDialogProps> = ({
  open,
  onClose,
  selectedLeads,
  onComplete,
}) => {
  const dispatch = useDispatchWrapper();

  const [selectedUser, setSelectedUser] = useState<ISelectOption | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleClose = () => {
    setSelectedUser(null);
    onClose();
  };

  const handleAssign = async () => {
    if (!selectedUser) return;

    const leadsUuids = selectedLeads
      .map((lead) => lead.leads_uuid)
      .filter((uuid): uuid is string => !!uuid);

    const payload = {
      replaced_by_uuid: String(selectedUser.value),
      replaced_by_name: selectedUser.label,
      replacing_uuid: leadsUuids.length === 1 ? leadsUuids : [],
      selective_leads_uuid: leadsUuids.length > 1 ? leadsUuids : [],
    };

    setSubmitting(true);
    try {
      await api.post("/leads/bulk-assign-leads", payload);
      dispatch(
        showMessage({
          type: "success",
          message: `Lead(s) assigned to ${selectedUser.label} successfully`,
          displayAs: "snackbar",
        }),
      );
      onComplete?.();
      handleClose();
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response?.data?.message || "Failed to assign lead(s)",
          displayAs: "snackbar",
        }),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      size="sm"
      title="Assign User"
      subtitle={`Assigning ${selectedLeads.length} selected lead(s)`}
      actions={[
        {
          type: "button",
          label: "Cancel",
          variant: "outlined",
          onClick: handleClose,
        },
        {
          type: "button",
          label: submitting ? "Assigning..." : "Assign",
          variant: "contained",
          color: "success",
          startIcon: <PersonAdd />,
          disabled: submitting || !selectedUser,
          onClick: handleAssign,
        },
      ]}
    >
      <Box sx={{ minHeight: "10rem" }}>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Select the user to assign the selected lead(s) to.
        </Typography>
        <UsersAutoSearchByRole
          label="Assign To"
          showTopLabel
          value={selectedUser?.value || ""}
          onChange={(_value, option) => setSelectedUser(option || null)}
        />
      </Box>
    </Dialog>
  );
};
